/**
 * Generic standardized paginated response types for AeroTrak (mirrors PPS backend pattern)
 * while providing resilience for legacy casing (Message/Data) and ad-hoc flat shapes.
 *
 * Always prefer the camelCase properties at consumption sites.
 */

export interface StandardPaginatedMeta {
  message: string[]; // unified camelCase list of messages
  page: number;
  pageSize: number;
  pages: number;
  total: number;
}

export interface StandardPaginatedObjectResponse<T>
  extends StandardPaginatedMeta {
  data: T | null;
}

export interface StandardPaginatedListResponse<T>
  extends StandardPaginatedMeta {
  data: T[]; // empty array if none
}

export type StandardResponse<TObj = any, TList = any> =
  | StandardPaginatedObjectResponse<TObj>
  | StandardPaginatedListResponse<TList>;

// Internal legacy compatible shape (PascalCase or old flat properties)
interface LegacyShape {
  Message?: string[] | string;
  Data?: any;
  message?: string[] | string;
  data?: any;
  Page?: number;
  page?: number;
  PageSize?: number;
  pageSize?: number;
  Pages?: number;
  pages?: number;
  Total?: number;
  total?: number;
  [k: string]: any; // allow unknown extra keys
}

/** Utility to coerce any backend payload into a standardized paginated response shape */
export function normalizeStandardResponse<T = any>(
  raw: LegacyShape | undefined | null,
  opts?: { assumeList?: boolean }
): StandardResponse<T, T> {
  if (!raw || typeof raw !== "object") {
    return {
      message: ["Empty response"],
      page: 0,
      pageSize: 0,
      pages: 0,
      total: 0,
      data: opts?.assumeList ? [] : null,
    } as StandardResponse<T, T>;
  }

  // Extract messages into a string array
  const rawMsg = (raw.message ?? raw.Message) as any;
  let message: string[] = [];
  if (Array.isArray(rawMsg)) message = rawMsg.filter(Boolean).map(String);
  else if (typeof rawMsg === "string") message = [rawMsg];
  if (!message.length) message = ["Request successful."];

  // Prefer camelCase numeric meta but fallback to PascalCase or defaults
  const page = coerceNumber(raw.page ?? raw.Page, 0);
  const pageSize = coerceNumber(raw.pageSize ?? raw.PageSize, 0);
  const pages = coerceNumber(
    raw.pages ?? raw.Pages,
    page > 0 && pageSize > 0
      ? Math.max(1, Math.ceil((raw.total ?? raw.Total ?? 0) / (pageSize || 1)))
      : 0
  );
  const total = coerceNumber(raw.total ?? raw.Total, 0);

  // Determine data candidate (camelCase or PascalCase)
  let candidateData: any =
    raw.data !== undefined
      ? raw.data
      : raw.Data !== undefined
        ? raw.Data
        : undefined;

  // Generic legacy flat shape inference (removed explicit hardcoded field list)
  if (candidateData === undefined) {
    const metaKeys = new Set([
      "Message",
      "message",
      "Data",
      "data",
      "Page",
      "page",
      "PageSize",
      "pageSize",
      "Pages",
      "pages",
      "Total",
      "total",
    ]);
    const flatEntries = Object.entries(raw).filter(
      ([k, v]) =>
        !metaKeys.has(k) && ["string", "number", "boolean"].includes(typeof v)
    );
    const hasIdLike = flatEntries.some(([k]) => /id$/i.test(k));
    const hasTimeLike = flatEntries.some(([k]) => /at$|time$/i.test(k));
    if (
      flatEntries.length &&
      (hasIdLike || hasTimeLike || flatEntries.length <= 5)
    ) {
      candidateData = flatEntries.reduce((acc, [k, v]) => {
        acc[toCamel(k)] = v;
        return acc;
      }, {} as any);
    }
  }

  if (candidateData === undefined) {
    candidateData = opts?.assumeList ? [] : null;
  }

  const isList =
    Array.isArray(candidateData) ||
    (opts?.assumeList && Array.isArray(candidateData));

  if (isList) {
    return {
      message,
      page,
      pageSize,
      pages,
      total,
      data: Array.isArray(candidateData) ? candidateData : [],
    } as StandardPaginatedListResponse<T>;
  }

  return {
    message,
    page,
    pageSize,
    pages,
    total,
    data: candidateData,
  } as StandardPaginatedObjectResponse<T>;
}

export function isStandardListResponse<T>(
  resp: StandardResponse<T>
): resp is StandardPaginatedListResponse<T> {
  return Array.isArray((resp as any).data);
}

export function isStandardObjectResponse<T>(
  resp: StandardResponse<T>
): resp is StandardPaginatedObjectResponse<T> {
  return !Array.isArray((resp as any).data);
}

function coerceNumber(v: any, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function toCamel(key: string): string {
  if (!key) return key;
  const cleaned = key.replace(/[-_]+([a-zA-Z0-9])/g, (_, c) => c.toUpperCase());
  return cleaned.charAt(0).toLowerCase() + cleaned.slice(1);
}

// Backward compatibility alias (if specific code expects AddUserAppPaginatedResponse style)
export type AddUserAppStandardResponse<T = any> =
  StandardPaginatedObjectResponse<T>;
